import React from "react";
import { Timeline, Typography, Spin, Collapse, Tag } from "antd";
import { BookingComment } from "../hooks/useBookingComments";
import { BookingAutomationTimelineEvent } from "../hooks/useBookingAutomationTimeline";
import { getAppAutomationLabel } from "../config/bookingAutomations";
import {
  formatAutomationTimelineDescription,
  formatAutomationTimelineTitle,
  isAutomaticAutomationOutcome,
} from "../utils/automationTimelineHelpers";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  ExclamationCircleOutlined,
  InboxOutlined as ArchiveOutlined,
  SafetyCertificateOutlined,
  CarOutlined,
  GiftOutlined,
  GlobalOutlined,
  StopOutlined,
  CommentOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

interface TimelineItem {
  id: string | number;
  type: "notification" | "comment" | "automation";
  content: string;
  timestamp: string;
  status?: string;
  description?: string;
  automatic?: boolean;
  automationOutcome?: string;
}

interface Notification {
  id: number;
  created_at: string;
  change_data: {
    new: {
      status: keyof typeof statusColors;
    };
    old?: {
      status?: string;
    };
  };
}

interface CombinedTimelineProps {
  comments: BookingComment[];
  notifications: Notification[];
  automationEvents?: BookingAutomationTimelineEvent[];
  loading: boolean;
}

const statusIcons: Record<keyof typeof statusColors, React.ReactNode> = {
  concept: <FileTextOutlined style={{ fontSize: 20 }} />,
  submitted: <ClockCircleOutlined style={{ fontSize: 20 }} />,
  approved: <CheckCircleOutlined style={{ fontSize: 20 }} />,
  rejected: <ExclamationCircleOutlined style={{ fontSize: 20 }} />,
  waiting_for_goods: <InboxOutlined style={{ fontSize: 20 }} />,
  goods_received_origin: <InboxOutlined style={{ fontSize: 20 }} />,
  customs_pending_clearance: (
    <SafetyCertificateOutlined style={{ fontSize: 20 }} />
  ),
  custom_clearance: <SafetyCertificateOutlined style={{ fontSize: 20 }} />,
  customs_cleared: <CheckCircleOutlined style={{ fontSize: 20 }} />,
  fyco: <GlobalOutlined style={{ fontSize: 20 }} />,
  out_for_delivery: <CarOutlined style={{ fontSize: 20 }} />,
  delivered: <GiftOutlined style={{ fontSize: 20 }} />,
  added_to_stock: (
    <InboxOutlined
      style={{ fontSize: 20, transform: "scale(1.1)", color: "#73d13d" }}
    />
  ),
  in_transit: <GlobalOutlined style={{ fontSize: 20 }} />,
  cancelled: <StopOutlined style={{ fontSize: 20 }} />,
  archived: <ArchiveOutlined style={{ fontSize: 20 }} />,
};

const statusColors: Record<string, string> = {
  concept: "#faad14", // Yellow - draft
  submitted: "#1890ff", // Blue - in process
  approved: "#52c41a", // Green - approved
  rejected: "#f5222d", // Red - rejected
  waiting_for_goods: "#36cfc9", // Cyan - waiting
  goods_received_origin: "#722ed1", // Purple - goods received
  customs_pending_clearance: "#722ed1", // Purple - customs pending
  custom_clearance: "#fa8c16", // Orange - customs clearance in progress
  customs_cleared: "#52c41a", // Green - customs cleared
  fyco: "#1890ff", // Blue - fyco
  out_for_delivery: "#13c2c2", // Cyan - delivery
  delivered: "#389e0d", // Green - delivered
  added_to_stock: "#73d13d", // Light green - added to stock
  in_transit: "#096dd9", // Blue - transit
  cancelled: "#ff4d4f", // Red - cancelled
  archived: "#8c8c8c", // Grey - archived
};

const isImportantUpdate = (notification: Notification): boolean => {
  const changeData = notification.change_data;
  if (!changeData || !changeData.new) return false;

  // Check if status has changed
  const hasStatusChange = Boolean(
    changeData.new.status && changeData.old?.status !== changeData.new.status
  );

  return hasStatusChange;
};

export const CombinedTimeline: React.FC<CombinedTimelineProps> = ({
  comments,
  notifications,
  automationEvents = [],
  loading,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <Spin />
      </div>
    );
  }

  // Filter out duplicate status updates
  const importantNotifications = notifications.filter(isImportantUpdate);

  // Combine and sort all items by timestamp
  const timelineItems: TimelineItem[] = [
    ...comments.map((comment) => ({
      id: comment.id,
      type: "comment" as const,
      content: comment.comment_types?.name || "",
      timestamp: comment.created_at,
      description: comment.description,
    })),
    ...importantNotifications.map((notification) => ({
      id: notification.id,
      type: "notification" as const,
      content: t(`status.${notification.change_data?.new?.status}`),
      timestamp: notification.created_at,
      status: notification.change_data?.new?.status,
    })),
    ...automationEvents.map((event) => {
      const label = getAppAutomationLabel(event.automation_id);
      return {
        id: `automation-${event.id}`,
        type: "automation" as const,
        content: formatAutomationTimelineTitle(event, label),
        timestamp: event.created_at,
        status: event.new_status ?? undefined,
        description: formatAutomationTimelineDescription(event, label),
        automatic: isAutomaticAutomationOutcome(event.outcome),
        automationOutcome: event.outcome,
      };
    }),
  ].sort((a, b) => dayjs(b.timestamp).unix() - dayjs(a.timestamp).unix());

  return (
    <Collapse defaultActiveKey={["1"]}>
      <Collapse.Panel header={t("bookings.timeline")} key="1">
        <Timeline>
          {timelineItems.map((item) => (
            <Timeline.Item
              key={item.id}
              color={
                item.type === "automation"
                  ? item.automationOutcome === "error"
                    ? "red"
                    : "#faad14"
                  : item.type === "notification" && item.status
                    ? statusColors[item.status]
                    : "blue"
              }
              dot={
                item.type === "automation" ? (
                  <ThunderboltOutlined style={{ fontSize: 20 }} />
                ) : item.type === "notification" && item.status ? (
                  statusIcons[item.status]
                ) : (
                  <CommentOutlined style={{ fontSize: "20px" }} />
                )
              }
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Text strong>{item.content}</Text>
                    {item.automatic && (
                      <Tag color="gold" style={{ margin: 0 }}>
                        Automatic
                      </Tag>
                    )}
                  </span>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    {dayjs(item.timestamp).format("DD MMM YYYY HH:mm")}
                  </Text>
                </div>
                {item.description && (
                  <Text type="secondary">{item.description}</Text>
                )}
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      </Collapse.Panel>
    </Collapse>
  );
};
